import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { ArrowLeft, Scale, ShieldCheck, Flag } from 'lucide-react';

const SECTIONS = [
  {
    t: '1. Objet',
    c: [
      "Les présentes conditions encadrent la proposition, la validation et la diffusion de vidéos courtes (« shorts ») par les membres de CodeVoiture.fr (élèves et enseignants) dans les rubriques « Shorts » et « Vidéos ».",
      "En soumettant une vidéo (fichier ou lien vers une plateforme autorisée), le contributeur accepte sans réserve les présentes conditions. L'acceptation est horodatée et conservée comme preuve.",
    ],
  },
  {
    t: '2. Qui peut proposer une vidéo — mineurs',
    c: [
      "La proposition est réservée aux membres disposant d'un compte (élève ou enseignant).",
      "Contributeur mineur : conformément à l'article 1145 du Code civil et au RGPD (art. 8), le mineur déclare avoir obtenu l'accord préalable d'un parent ou représentant légal avant toute soumission. CodeVoiture.fr pourra retirer tout contenu sur simple demande du représentant légal.",
    ],
  },
  {
    t: "3. Droits d'auteur (Code de la propriété intellectuelle)",
    c: [
      "Le contributeur garantit être l'auteur de la vidéo ou disposer de l'intégralité des droits nécessaires à sa diffusion (art. L111-1 et L122-4 du CPI). La reprise d'un contenu tiers (musique, extraits, images) sans autorisation constitue une contrefaçon.",
      "Pour les liens (YouTube, Vimeo, Dailymotion, TikTok, Instagram), le contributeur garantit que la vidéo liée respecte les conditions de la plateforme d'origine et qu'il est habilité à la partager.",
      "Important : proposer un lien ne vaut pas autorisation de republication. La vidéo liée reste diffusée via le lecteur de sa plateforme d'origine, sans copie ni réhébergement. Si le contributeur est l'auteur de la vidéo et souhaite un hébergement direct sur CodeVoiture.fr (envoi de fichier), l'acceptation des présentes conditions vaut autorisation de diffusion au sens de l'article 5.",
    ],
  },
  {
    t: "4. Droit à l'image et données des tiers",
    c: [
      "Toute personne identifiable dans la vidéo (élève, moniteur, piéton, autre conducteur) doit avoir donné son accord exprès à la captation et à la diffusion (art. 9 du Code civil). À défaut, les visages doivent être floutés.",
      "Les plaques d'immatriculation de véhicules tiers doivent être floutées ou non lisibles dans la mesure du possible.",
      "Pour un élève mineur filmé, l'accord écrit des deux parents ou du représentant légal est requis.",
    ],
  },
  {
    t: '5. Licence accordée à CodeVoiture.fr',
    c: [
      "Le contributeur concède à CodeVoiture.fr une licence gratuite, non exclusive et pour le monde entier, limitée à la diffusion de la vidéo sur le site (rubriques Shorts et Vidéos) et à sa promotion sur le site.",
      "Cette licence n'emporte aucune cession de droits : le contributeur reste pleinement propriétaire de sa vidéo et peut la diffuser ailleurs librement.",
      "Retrait : le contributeur peut demander à tout moment le retrait de sa vidéo (depuis son espace ou par le formulaire de contact). Le retrait est effectué dans les meilleurs délais, au plus tard sous 7 jours.",
    ],
  },
  {
    t: '6. Modération et publication',
    c: [
      "Chaque vidéo proposée est placée en attente et n'est visible de personne avant validation expresse par l'équipe d'administration.",
      "La publication n'est jamais garantie : l'équipe peut refuser une vidéo sans avoir à justifier sa décision, et peut dépublier ou retirer à tout moment une vidéo déjà publiée.",
      "La modération ne constitue pas une validation juridique du contenu : le contributeur demeure seul responsable de sa vidéo.",
    ],
  },
  {
    t: '7. Contenus interdits',
    c: [
      "Sont refusés et peuvent entraîner la suppression du compte : les vidéos valorisant une infraction au Code de la route (excès de vitesse, rodéo, téléphone au volant…), une conduite dangereuse ou mettant en danger autrui ;",
      "les contenus publicitaires ou d'autopromotion commerciale non autorisés ; les contenus violents, haineux, discriminatoires, à caractère sexuel, ou portant atteinte à la dignité des personnes ;",
      "les contenus trompeurs sur la réglementation routière française en vigueur.",
    ],
  },
  {
    t: '8. Signalement (loi LCEN)',
    c: [
      "Conformément à l'article 6 de la loi n°2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique (LCEN), tout visiteur peut signaler une vidéo publiée qui lui paraît illicite via le bouton « Signaler » de la galerie.",
      "Chaque signalement est transmis immédiatement à l'équipe d'administration qui l'examine et retire promptement tout contenu manifestement illicite.",
    ],
  },
  {
    t: '9. Données personnelles (RGPD)',
    c: [
      "Données traitées lors d'une proposition : nom/prénom du compte, adresse e-mail, rôle (élève/enseignant), vidéo, titre, description, horodatage d'acceptation des présentes conditions.",
      "Finalité : gestion de la modération et de la diffusion des vidéos communautaires. Base légale : exécution des présentes conditions (art. 6.1.b RGPD).",
      "Durée : les données sont conservées tant que la vidéo est en ligne, puis 12 mois à des fins de preuve. Droits d'accès, de rectification, d'effacement et d'opposition : via le formulaire de contact du site.",
    ],
  },
  {
    t: '10. Responsabilité',
    c: [
      "Le contributeur garantit CodeVoiture.fr contre tout recours de tiers (droits d'auteur, droit à l'image, diffamation…) lié à la vidéo proposée.",
      "CodeVoiture.fr agit en qualité d'hébergeur pour les contenus communautaires au sens de la LCEN et ne saurait être tenu responsable des contenus avant d'en avoir eu effectivement connaissance.",
      "Les vidéos liées restent hébergées sur leur plateforme d'origine (YouTube, Vimeo…), soumises aux conditions de celle-ci.",
    ],
  },
];

export default function ConditionsVideosPage() {
  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 mt-8 sm:mt-12 pb-16" data-testid="conditions-videos-page">
        <Link to="/shorts/proposer" className="inline-flex items-center gap-1.5 text-white/50 text-[12.5px] hover:text-white mb-4">
          <ArrowLeft size={13} /> Retour à la proposition
        </Link>
        <SectionTitle
          kicker="Communauté · Cadre juridique"
          title="Conditions de contribution vidéo."
          sub="Ce que tu acceptes en proposant un short ou un lien vidéo : droits d'auteur, droit à l'image, modération, RGPD. Version du 1er juin 2026."
        />
        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          {[
            { icon: Scale, txt: 'Tu restes propriétaire de ta vidéo' },
            { icon: ShieldCheck, txt: 'Rien ne se publie sans validation' },
            { icon: Flag, txt: 'Retrait possible à tout moment' },
          ].map(({ icon: Icon, txt }) => (
            <div key={txt} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5 flex items-center gap-2.5">
              <Icon size={16} className="text-red-400 shrink-0" />
              <span className="text-white/75 text-[12px] leading-snug">{txt}</span>
            </div>
          ))}
        </div>
        <div className="mt-8 space-y-7">
          {SECTIONS.map((s) => (
            <section key={s.t}>
              <h2 className="font-serif-editorial text-white text-[19px] mb-2.5">{s.t}</h2>
              <div className="space-y-2">
                {s.c.map((p, i) => (
                  <p key={i} className="text-white/65 text-[13.5px] leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border border-red-900/40 bg-red-950/15 p-5">
          <p className="text-white/70 text-[13px] leading-relaxed">
            Une question sur tes droits ou une demande de retrait ? Écris-nous via la page{' '}
            <Link to="/contact" className="text-red-400 font-semibold hover:text-red-300">Nous contacter</Link>. Textes de référence : Code de la propriété intellectuelle (L111-1, L122-4), Code civil (art. 9), loi LCEN du 21 juin 2004 (art. 6), RGPD (UE) 2016/679.
          </p>
        </div>
      </div>
    </PageShell>
  );
}
